import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useWritable } from '../../app/MeContext';
import { useScreen, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { showConfirm } from '../../lib/telegram';
import { Field, FormSkeleton, LoadFailed, StickySave, useSaveOrLeave } from '../org-parts';
import { useAdminSettings, useLocationMutations } from '../api';

type Draft = { title: string; address: string; comment: string; primary: boolean };

const EMPTY: Draft = { title: '', address: '', comment: '', primary: false };

function errorOf(name: 'title' | 'address' | 'comment', value: string): string | null {
  const v = value.trim();
  if (name === 'title') {
    if (!v) return 'Как называть этот адрес внутри организации';
    if (v.length < 2 || v.length > 40) return 'От 2 до 40 символов';
    return null;
  }
  if (name === 'address') {
    if (!v) return 'Без адреса клиент не найдёт, куда идти';
    if (v.length < 5 || v.length > 200) return 'От 5 до 200 символов';
    return null;
  }
  return v.length > 200 ? 'Не длиннее 200 символов' : null;
}

/**
 * Подэкран адреса — новый или существующий. Сохраняется автономно: на кнопку
 * основного экрана не влияет, потому что адрес живёт отдельной записью, а не
 * полем карточки организации.
 */
export function OrgAddress() {
  const writable = useWritable();
  const navigate = useNavigate();
  const toast = useToast();
  const { id: raw } = useParams();
  const isNew = raw === 'new';
  const id = Number(raw);

  const data = useAdminSettings();
  const { create, update, remove } = useLocationMutations();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Partial<Record<'title' | 'address' | 'comment', string>>>({});

  const locations = data.data?.locations ?? [];
  const current = isNew ? null : locations.find((l) => l.id === id) ?? null;
  const only = isNew ? locations.length === 0 : locations.length === 1;

  // Черновик собирается из загруженного адреса один раз: правки не должны
  // откатываться фоновым перезапросом списка.
  const loaded: Draft | null = isNew
    ? EMPTY
    : current
      ? { title: current.title, address: current.address, comment: current.comment, primary: !!current.is_primary }
      : null;
  const form = draft ?? loaded;

  const dirty = !!form && !!loaded && JSON.stringify(form) !== JSON.stringify(loaded);
  const busy = create.isPending || update.isPending || remove.isPending;

  useScreen(isNew ? 'Новый адрес' : 'Адрес', null);
  useSaveOrLeave(dirty, () => submit());

  if (data.isError) {
    return <Screen><LoadFailed busy={data.isFetching} onRetry={() => void data.refetch()} /></Screen>;
  }
  if (!form) {
    return <Screen><FormSkeleton rows={3} /></Screen>;
  }

  const locked = !writable || busy;
  // Единственному адресу выбирать нечего: без основного очным услугам некуда привязаться.
  const primary = only ? true : form.primary;
  const wasPrimary = locations.find((l) => l.is_primary && l.id !== id);

  const patch = (p: Partial<Draft>) => setDraft({ ...form, ...p });
  const check = (name: 'title' | 'address' | 'comment', value: string) =>
    setErrors((prev) => ({ ...prev, [name]: errorOf(name, value) ?? undefined }));

  /**
   * Обещание возвращается наружу: сторож ухода с экрана предлагает сохранить и
   * ждёт ответа сервера, прежде чем уводить, — иначе мутация уехала бы вместе
   * с экраном, который её создал, и правка пропала бы молча.
   */
  function submit(): Promise<unknown> {
    if (!form) return Promise.resolve();
    const found = {
      title: errorOf('title', form.title) ?? undefined,
      address: errorOf('address', form.address) ?? undefined,
      comment: errorOf('comment', form.comment) ?? undefined,
    };
    setErrors(found);
    if (found.title || found.address || found.comment) return Promise.reject(new Error('VALIDATION'));

    const body = {
      title: form.title.trim(),
      address: form.address.trim(),
      comment: form.comment.trim(),
      isPrimary: primary,
    };
    const done = (): void => {
      setDraft(null);
      toast('Изменения сохранены', { ms: 2000 });
      navigate('/admin/org/addresses');
    };
    const failed = (e: unknown): never => {
      toast(e instanceof ApiError ? e.message : 'Не получилось сохранить');
      throw e;
    };
    return isNew
      ? create.mutateAsync(body).then(done, failed)
      : update.mutateAsync({ id, ...body }).then(done, failed);
  }

  async function del() {
    if (!current) return;
    const ok = await showConfirm(`Удалить адрес «${current.title}»?`, { title: 'Удаление', destructive: true });
    if (!ok) return;
    remove.mutate(current.id, {
      onSuccess: () => {
        setDraft(null);
        toast('Адрес удалён');
        navigate('/admin/org/addresses');
      },
      onError: (e) => toast(
        e instanceof ApiError && e.is('LAST_LOCATION')
          ? 'Нужен хотя бы один адрес'
          : 'Не получилось удалить',
      ),
    });
  }

  return (
    <Screen className="orgform sticky-save">
      <Field label="Название" error={errors.title}>
        {(id_, bad) => (
          <input
            id={id_} type="text" placeholder="Троицк" value={form.title} disabled={locked} aria-invalid={bad}
            onChange={(e) => patch({ title: e.target.value })}
            onBlur={(e) => check('title', e.target.value)}
          />
        )}
      </Field>

      <Field label="Адрес строкой" error={errors.address}>
        {(id_, bad) => (
          <input
            id={id_} type="text" placeholder="ул. Садовая, 12, каб. 4"
            value={form.address} disabled={locked} aria-invalid={bad}
            onChange={(e) => patch({ address: e.target.value })}
            onBlur={(e) => check('address', e.target.value)}
          />
        )}
      </Field>

      <div className="kv">
        <div className="kv-row">
          <span className="k">
            Основной
            <small>
              {only
                ? 'Единственный адрес всегда основной'
                : primary && wasPrimary
                  ? `«${wasPrimary.title}» перестанет быть основным`
                  : 'По умолчанию для очных услуг'}
            </small>
          </span>
          <span className="v">
            <button
              className="switch"
              type="button"
              aria-pressed={primary}
              aria-label="Основной адрес"
              disabled={locked || only}
              onClick={() => patch({ primary: !form.primary })}
            />
          </span>
        </div>
      </div>

      <Field
        label="Комментарий для клиента"
        error={errors.comment}
        hint="Показываем в подтверждении записи и в напоминании"
        counter={`${form.comment.length}/200`}
      >
        {(id_, bad) => (
          <textarea
            id={id_} className="grow" rows={2} placeholder="вход со двора, 2 этаж"
            value={form.comment} disabled={locked} aria-invalid={bad}
            onChange={(e) => patch({ comment: e.target.value })}
            onBlur={(e) => check('comment', e.target.value)}
          />
        )}
      </Field>

      {writable ? (
        <>
          {/* Удаление стоит до липкой панели: панель прижата к низу и накрыла бы
              всё, что оказалось под ней. */}
          {current ? (
            <div className="danger-zone">
              <button className="linkish danger" type="button" disabled={only || busy} onClick={() => void del()}>
                Удалить адрес
              </button>
              {only ? <span className="hint">Нужен хотя бы один адрес</span> : null}
            </div>
          ) : null}

          {/* Новый адрес сохраняется и «без изменений» — пустая форма и есть
              то, что человек пришёл заполнить; у существующего кнопка ждёт правок. */}
          <StickySave
            dirty={isNew || dirty}
            busy={busy}
            failed={create.isError || update.isError}
            onClick={() => { void submit().catch(() => {}); }}
          />
        </>
      ) : null}
    </Screen>
  );
}
